import React from 'react';
import type {Intent} from '@sail/engine';
import {useIsomorphicLayoutEffect} from '@sail/engine';
import {useTokensOverrides} from './useTokensOverrides';
import {useThemeOverride} from './useThemeOverride';
import {SAIL_TOOLKIT_ACTIVE} from './constants';
import {messenger} from './messenger';

export function useSailToolkit(): Intent[] | undefined {
  const [ready, setReady] = React.useState(false);
  const tokensOverrides = useTokensOverrides(ready);
  const themeOverrides = useThemeOverride(ready);

  useIsomorphicLayoutEffect(() => {
    if (!SAIL_TOOLKIT_ACTIVE || !messenger) {
      return;
    }

    // `useIsomorphicLayoutEffect` is a no-op on the server, so `ready` stays
    // `false` during SSR and matches the initial client render. Setting it
    // outside an effect would cause the server to render different output than
    // the client's first pass, producing a React hydration mismatch.
    setReady(true);

    messenger.sendMessage('SAIL_UI_READY', {});

    return messenger.onMessageType('SAIL_PROBE', () => {
      messenger?.sendMessage('SAIL_UI_READY', {});
    });
  }, []);

  // Themes first (composed in selection order), then token overrides, so manual
  // token tweaks win over the selected themes.
  const intents = React.useMemo(
    () => [...themeOverrides, tokensOverrides],
    [themeOverrides, tokensOverrides],
  );

  return ready ? intents : undefined;
}
